import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Mail, ArrowLeft } from "lucide-react";
import logoImage from "@assets/logo_1753388790228.png";
import { useLocation } from "wouter";

export default function ForgotPassword() {
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();

  // Add auth-page class to body to hide support widget
  useEffect(() => {
    document.body.classList.add('auth-page');
    return () => {
      document.body.classList.remove('auth-page');
    };
  }, []);

  // Pre-fill email from URL parameter if provided
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get("email");
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, []);

  const requestResetMutation = useMutation({
    mutationFn: async (email: string) => {
      // Get CSRF token first
      const csrfResponse = await fetch("/api/csrf-token", {
        credentials: "include",
      });
      const { csrfToken } = await csrfResponse.json();

      const response = await fetch("/api/auth/request-password-reset", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw errorData;
      }

      return response.json();
    },
    onSuccess: () => {
      setEmailSent(true);
      toast({
        title: "Reset Link Sent",
        description: "Check your email for password reset instructions.",
      });
    },
    onError: (error: any) => {
      const message = error.message || "Failed to send reset email";
      toast({
        title: "Request Failed",
        description: message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    requestResetMutation.mutate(email);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900 flex items-center justify-center px-4 py-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-4">
          <div className="flex items-center justify-center space-x-3 mb-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
            <img
              src={logoImage}
              alt="TechAlign"
              className="h-16 w-16 object-contain"
            />
            <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              TechAlign
            </h1>
          </div>
        </div>

        <Card className="shadow-lg border-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
                <Mail className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <CardTitle className="text-lg font-bold">
              {emailSent ? "Check Your Email" : "Forgot Password?"}
            </CardTitle>
            <p className="text-muted-foreground">
              {emailSent
                ? "We've sent you a password reset link"
                : "Enter your email to receive a password reset link"}
            </p>
          </CardHeader>

          <CardContent>
            {emailSent ? (
              <div className="space-y-3">
                <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                  <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <AlertDescription className="text-green-800 dark:text-green-200">
                    If an account exists with <strong>{email}</strong>, you will
                    receive a password reset link shortly. Please check your
                    inbox and spam folder.
                  </AlertDescription>
                </Alert>

                <div className="text-center space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Didn't receive the email?
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEmailSent(false);
                      requestResetMutation.mutate(email);
                    }}
                    disabled={requestResetMutation.isPending}
                    className="w-full"
                  >
                    Resend Email
                  </Button>
                </div>

                <div className="text-center pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setLocation("/auth")}
                    className="text-sm"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Login
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    className="h-11"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 text-base font-medium bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={requestResetMutation.isPending}
                >
                  {requestResetMutation.isPending
                    ? "Sending..."
                    : "Send Reset Link"}
                </Button>

                <div className="text-center pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setLocation("/auth")}
                    className="text-sm"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Login
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
